/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save } from 'lucide-react';
import { useUpdateTemplateMetadata } from '@/hooks/api/useTemplateCatalog';
import { useNotification } from '@/hooks/useNotification';
import { TemplateCatalogItem } from '@/types/superadmin.types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  template: TemplateCatalogItem | null;
}

export default function EditTemplateModal({ isOpen, onClose, template }: Props) {
  const { t } = useTranslation('platform');
  const { success, error: showError } = useNotification();
  const updateMutation = useUpdateTemplateMetadata();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPremium, setIsPremium] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description || '');
      setIsPremium(template.isPremium);
      setIsActive(template.isActive);
      setSortOrder(template.sortOrder);
    }
  }, [template]);

  if (!isOpen || !template) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateMutation.mutateAsync({
        id: template.id,
        data: { name, description, isPremium, isActive, sortOrder },
      });
      success(t('templateCatalog.edit.success'));
      onClose();
    } catch (err: any) {
      showError(err?.message || t('templateCatalog.edit.error'));
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal-container">
        <div className="modal-header">
          <h2>{t('templateCatalog.edit.title')}</h2>
          <button onClick={onClose} className="modal-close"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>{t('templateCatalog.fields.code')}</label>
            <input type="text" value={template.code} disabled className="form-input" style={{ opacity: 0.6 }} />
          </div>

          <div className="form-group">
            <label>{t('templateCatalog.fields.name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={100}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{t('templateCatalog.fields.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{t('templateCatalog.fields.sortOrder')}</label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              min={0}
              className="form-input"
            />
          </div>

          <div className="form-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isPremium}
                onChange={(e) => setIsPremium(e.target.checked)}
              />
              {t('templateCatalog.fields.premium')}
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              {t('templateCatalog.fields.active')}
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={updateMutation.isPending}>
              <Save size={14} />
              {updateMutation.isPending ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
      <style>{modalStyles}</style>
    </>
  );
}

const modalStyles = `
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    z-index: 1000;
  }
  .modal-container {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 480px;
    max-height: 90vh;
    overflow-y: auto;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-radius: 12px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    z-index: 1001;
  }
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }
  .modal-header h2 { font-size: 16px; font-weight: 600; color: var(--color-text-primary); margin: 0; }
  .modal-close { background: none; border: none; color: var(--color-text-muted); cursor: pointer; padding: 4px; border-radius: 6px; }
  .modal-close:hover { background: var(--color-bg-hover); color: var(--color-text-primary); }
  .modal-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
  .modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding-top: 12px; border-top: 1px solid var(--color-border-subtle); }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group label { font-size: 12px; font-weight: 500; color: var(--color-text-secondary); }
  .form-input { padding: 8px 12px; border: 1px solid var(--color-border-default); border-radius: 8px; background: var(--color-bg-primary); color: var(--color-text-primary); font-size: 13px; outline: none; }
  .form-input:focus { border-color: var(--color-brand); }
  textarea.form-input { resize: vertical; min-height: 60px; }
  .form-row { display: flex; gap: 24px; }
  .checkbox-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: var(--color-text-secondary); }
  .checkbox-label input[type="checkbox"] { width: 16px; height: 16px; accent-color: var(--color-brand); }
  .btn-primary { display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: var(--color-brand); color: white; border: none; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; }
  .btn-primary:hover { opacity: 0.9; }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-secondary { padding: 8px 16px; background: transparent; color: var(--color-text-secondary); border: 1px solid var(--color-border-default); border-radius: 8px; font-size: 13px; cursor: pointer; }
  .btn-secondary:hover { background: var(--color-bg-hover); }
`;
